import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
	type Matter,
	type RestrictedWord,
	type RestrictedWordRule,
	useMattersCache,
	useRestrictedWords,
} from "@/shared/cache";
import { ComboBox } from "@/shared/components/ComboBox/ComboBox";
import { NarrativeTextArea } from "@/shared/components/NarrativeTextArea/NarrativeTextArea";
import { Button } from "@/shared/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Switch } from "@/shared/components/ui/switch";
import { Textarea } from "@/shared/components/ui/textarea";
import { useApi } from "@/shared/utils/request";
import { detectRestrictedWords } from "@/shared/utils/restrictedWords";
import styles from "./AdminViews.module.css";

type RuleFormState = {
	regexPattern: string;
	displayName: string;
	suggestion: string;
	clientCode: string;
	matterId: string;
	warning: boolean;
	disabled: boolean;
};

const EMPTY_FORM: RuleFormState = {
	regexPattern: "",
	displayName: "",
	suggestion: "",
	clientCode: "",
	matterId: "",
	warning: false,
	disabled: false,
};

type ClientOption = {
	code: string;
	name: string;
};

const clientSearchFields = [
	(client: ClientOption) => ({ value: client.code, weight: 1.5 }),
	(client: ClientOption) => ({ value: client.name, weight: 1 }),
];

const matterSearchFields = [
	(matter: Matter) => ({
		value: matterScopeParts(matter).matterId,
		weight: 1.5,
	}),
	(matter: Matter) => ({ value: matter.matter ?? "", weight: 1.2 }),
	(matter: Matter) => ({ value: matter.description, weight: 1 }),
];

function matterScopeParts(matter: Matter): {
	clientCode: string;
	matterId: string;
} {
	const [clientCode = "", ...matterParts] = matter.matter_id.split(".");
	return {
		clientCode: clientCode.trim(),
		matterId: matterParts.join(".").trim(),
	};
}

function clientOptionLabel(client: ClientOption): string {
	return client.name && client.name !== client.code
		? `${client.code} — ${client.name}`
		: client.code;
}

function matterOptionLabel(matter: Matter): string {
	const { matterId } = matterScopeParts(matter);
	const description = matter.description.trim();
	return description ? `${matterId} — ${description}` : matterId;
}

function formStateForRule(rule: RestrictedWordRule | null): RuleFormState {
	if (!rule) return EMPTY_FORM;
	return {
		regexPattern: rule.regexPattern,
		displayName: rule.displayName,
		suggestion: rule.suggestion ?? "",
		clientCode: rule.clientCode ?? "",
		matterId: rule.matterId ?? "",
		warning: rule.warning,
		disabled: rule.disabled,
	};
}

function scopeLabel(rule: RestrictedWordRule): string {
	if (rule.clientCode && rule.matterId) {
		return `${rule.clientCode} / ${rule.matterId}`;
	}
	if (rule.clientCode) return rule.clientCode;
	return "Firm-wide";
}

function RuleDialog({
	open,
	rule,
	onOpenChange,
	onSaved,
}: {
	open: boolean;
	rule: RestrictedWordRule | null;
	onOpenChange: (open: boolean) => void;
	onSaved: () => Promise<void>;
}) {
	const { makeApiRequest } = useApi();
	const { matters, isLoading: mattersLoading } = useMattersCache();
	const [form, setForm] = useState<RuleFormState>(EMPTY_FORM);
	const [testNarrative, setTestNarrative] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [formError, setFormError] = useState<string | null>(null);
	const draftRuleTest = useMemo<{
		restrictedWords: RestrictedWord[];
		error: string | null;
	}>(() => {
		const regexPattern = form.regexPattern.trim();
		if (!regexPattern) return { restrictedWords: [], error: null };

		try {
			return {
				restrictedWords: [
					{
						word: regexPattern,
						pattern: new RegExp(regexPattern, "gi"),
						displayName: form.displayName.trim() || "Draft rule",
						suggestion: form.suggestion.trim() || null,
						severity: form.warning ? "warning" : "block",
					},
				],
				error: null,
			};
		} catch (error) {
			return {
				restrictedWords: [],
				error:
					error instanceof Error
						? `Invalid regular expression: ${error.message}`
						: "Invalid regular expression.",
			};
		}
	}, [form.displayName, form.regexPattern, form.suggestion, form.warning]);
	const testMatches = useMemo(
		() => detectRestrictedWords(testNarrative, draftRuleTest.restrictedWords),
		[draftRuleTest.restrictedWords, testNarrative],
	);
	const clientOptions = useMemo(() => {
		const clientsByCode = new Map<string, ClientOption>();
		for (const matter of matters) {
			const { clientCode } = matterScopeParts(matter);
			if (!clientCode || clientsByCode.has(clientCode)) continue;
			clientsByCode.set(clientCode, {
				code: clientCode,
				name: matter.client.trim(),
			});
		}
		return [...clientsByCode.values()].sort((left, right) =>
			left.code.localeCompare(right.code),
		);
	}, [matters]);
	const matterOptions = useMemo(
		() =>
			matters
				.filter((matter) => {
					const scope = matterScopeParts(matter);
					return scope.clientCode === form.clientCode && !!scope.matterId;
				})
				.sort((left, right) =>
					matterScopeParts(left).matterId.localeCompare(
						matterScopeParts(right).matterId,
					),
				),
		[form.clientCode, matters],
	);

	useEffect(() => {
		if (!open) return;
		setForm(formStateForRule(rule));
		setTestNarrative("");
		setFormError(null);
	}, [open, rule]);

	const update = <K extends keyof RuleFormState>(
		field: K,
		value: RuleFormState[K],
	) => setForm((current) => ({ ...current, [field]: value }));

	const submit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const regexPattern = form.regexPattern.trim();
		const displayName = form.displayName.trim();
		const clientCode = form.clientCode.trim() || null;
		const matterId = form.matterId.trim() || null;

		if (!regexPattern || !displayName) {
			setFormError("Pattern and display name are required.");
			return;
		}
		if (draftRuleTest.error) {
			setFormError(draftRuleTest.error);
			return;
		}
		if (matterId && !clientCode) {
			setFormError("A client code is required for a matter-specific rule.");
			return;
		}

		setSubmitting(true);
		setFormError(null);
		try {
			const response = await makeApiRequest({
				endpoint: rule
					? `/v2/restricted-words/${encodeURIComponent(rule.id)}`
					: "/v2/restricted-words",
				method: rule ? "PATCH" : "POST",
				body: {
					regexPattern,
					displayName,
					suggestion: form.suggestion.trim() || null,
					warning: form.warning,
					disabled: form.disabled,
					clientCode,
					matterId,
				},
			});
			if (response.status === "ERROR") {
				setFormError(response.error || "Failed to save restricted word.");
				return;
			}

			await onSaved();
			toast.success(
				rule ? "Restricted word updated" : "Restricted word created",
			);
			onOpenChange(false);
		} catch (error) {
			setFormError(
				error instanceof Error
					? error.message
					: "Failed to save restricted word.",
			);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="overflow-visible sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>
						{rule ? "Edit restricted word" : "Add restricted word"}
					</DialogTitle>
					<DialogDescription>
						Patterns are regular expressions. Leave both scope fields empty for
						a firm-wide rule.
					</DialogDescription>
				</DialogHeader>

				<form className="space-y-4" onSubmit={submit}>
					<div className="space-y-2 rounded-md border border-border p-3">
						<div className="flex flex-wrap items-baseline justify-between gap-2">
							<Label htmlFor="restricted-word-test-narrative">
								Test narrative
							</Label>
							<span className="text-xs text-muted-foreground">
								Tests only this draft rule
							</span>
						</div>
						<NarrativeTextArea
							id="restricted-word-test-narrative"
							value={testNarrative}
							onChange={setTestNarrative}
							placeholder="Type or paste a narrative to test the pattern"
							restrictedWords={draftRuleTest.restrictedWords}
							error={draftRuleTest.error ?? undefined}
							className="min-h-20 max-h-28 resize-y"
						/>
						{!draftRuleTest.error && !form.regexPattern.trim() && (
							<p className="text-xs text-muted-foreground">
								Enter a regex pattern to begin testing.
							</p>
						)}
						{!draftRuleTest.error &&
							!!form.regexPattern.trim() &&
							!!testNarrative && (
								<output
									className={
										testMatches.length === 0
											? "text-xs text-emerald-600 dark:text-emerald-400"
											: form.warning
												? "text-xs text-amber-600 dark:text-amber-400"
												: "text-xs text-destructive"
									}
								>
									{testMatches.length === 0
										? "No matches for this rule."
										: `${form.warning ? "Warning" : "Blocked"}: ${testMatches.length} ${testMatches.length === 1 ? "match" : "matches"}.`}
								</output>
							)}
					</div>

					<div className="grid gap-4 sm:grid-cols-2">
						<div className="space-y-1.5">
							<Label htmlFor="restricted-word-name">Display name</Label>
							<Input
								id="restricted-word-name"
								value={form.displayName}
								onChange={(event) => update("displayName", event.target.value)}
								maxLength={100}
								required
							/>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="restricted-word-pattern">Regex pattern</Label>
							<Input
								id="restricted-word-pattern"
								className="font-mono"
								value={form.regexPattern}
								onChange={(event) => update("regexPattern", event.target.value)}
								maxLength={255}
								required
							/>
						</div>
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="restricted-word-suggestion">Suggestion</Label>
						<Textarea
							id="restricted-word-suggestion"
							value={form.suggestion}
							onChange={(event) => update("suggestion", event.target.value)}
							maxLength={500}
							placeholder="Optional replacement guidance"
						/>
					</div>

					<div className="grid gap-4 sm:grid-cols-2">
						<div className="space-y-1.5">
							<Label htmlFor="restricted-word-client">Client code</Label>
							<ComboBox
								id="restricted-word-client"
								items={clientOptions}
								value={form.clientCode || null}
								onChange={(clientCode) =>
									setForm((current) => ({
										...current,
										clientCode,
										matterId: "",
									}))
								}
								fields={clientSearchFields}
								getKey={(client) => client.code}
								getDisplayLabel={clientOptionLabel}
								placeholder="Select a client (optional)"
								disabled={mattersLoading}
								disabledMessage="Loading clients…"
								clearable
								onClear={() =>
									setForm((current) => ({
										...current,
										clientCode: "",
										matterId: "",
									}))
								}
							/>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="restricted-word-matter">Matter ID</Label>
							<ComboBox
								id="restricted-word-matter"
								items={matterOptions}
								value={form.matterId || null}
								onChange={(matterId) => update("matterId", matterId)}
								fields={matterSearchFields}
								getKey={(matter) => matterScopeParts(matter).matterId}
								getDisplayLabel={matterOptionLabel}
								placeholder="Select a matter (optional)"
								disabled={!form.clientCode || mattersLoading}
								disabledMessage={
									mattersLoading ? "Loading matters…" : "Select a client first"
								}
								clearable
								onClear={() => update("matterId", "")}
							/>
						</div>
					</div>

					<div className="grid gap-3 rounded-md border border-border p-3 sm:grid-cols-2">
						<label
							htmlFor="restricted-word-warning"
							className="flex items-center justify-between gap-3 text-sm"
						>
							<span>
								<span className="block font-medium">Warning only</span>
								<span className="text-xs text-muted-foreground">
									Allow input but show a warning
								</span>
							</span>
							<Switch
								id="restricted-word-warning"
								checked={form.warning}
								onCheckedChange={(checked) => update("warning", checked)}
							/>
						</label>
						<label
							htmlFor="restricted-word-disabled"
							className="flex items-center justify-between gap-3 text-sm"
						>
							<span>
								<span className="block font-medium">Disabled</span>
								<span className="text-xs text-muted-foreground">
									Keep the rule without enforcing it
								</span>
							</span>
							<Switch
								id="restricted-word-disabled"
								checked={form.disabled}
								onCheckedChange={(checked) => update("disabled", checked)}
							/>
						</label>
					</div>

					{formError && (
						<p className="text-sm text-destructive" role="alert">
							{formError}
						</p>
					)}

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
						>
							Cancel
						</Button>
						<Button type="submit" loading={submitting}>
							{rule ? "Save changes" : "Create rule"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}

export default function RestrictedWordsView() {
	const { makeApiRequest } = useApi();
	const { rules, isLoading, error, refresh } = useRestrictedWords();
	const [search, setSearch] = useState("");
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editingRule, setEditingRule] = useState<RestrictedWordRule | null>(
		null,
	);
	const [mutatingId, setMutatingId] = useState<string | null>(null);

	const filteredRules = useMemo(() => {
		const query = search.trim().toLowerCase();
		return rules
			.filter((rule) => {
				if (!query) return true;
				return [
					rule.displayName,
					rule.regexPattern,
					rule.suggestion,
					rule.clientCode,
					rule.matterId,
				].some((value) => value?.toLowerCase().includes(query));
			})
			.sort((left, right) => left.displayName.localeCompare(right.displayName));
	}, [rules, search]);

	const openCreate = () => {
		setEditingRule(null);
		setDialogOpen(true);
	};

	const openEdit = (rule: RestrictedWordRule) => {
		setEditingRule(rule);
		setDialogOpen(true);
	};

	const updateDisabled = async (
		rule: RestrictedWordRule,
		disabled: boolean,
	) => {
		setMutatingId(rule.id);
		try {
			const response = await makeApiRequest({
				endpoint: `/v2/restricted-words/${encodeURIComponent(rule.id)}`,
				method: "PATCH",
				body: { disabled },
			});
			if (response.status === "ERROR") {
				throw new Error(response.error || "Failed to update restricted word.");
			}
			await refresh();
			toast.success(
				disabled ? "Restricted word disabled" : "Restricted word enabled",
			);
		} catch (mutationError) {
			toast.error(
				mutationError instanceof Error
					? mutationError.message
					: "Failed to update restricted word.",
			);
		} finally {
			setMutatingId(null);
		}
	};

	const deleteRule = async (rule: RestrictedWordRule) => {
		if (
			!window.confirm(
				`Permanently delete “${rule.displayName}”? This cannot be undone.`,
			)
		) {
			return;
		}

		setMutatingId(rule.id);
		try {
			const response = await makeApiRequest({
				endpoint: `/v2/restricted-words/${encodeURIComponent(rule.id)}`,
				method: "DELETE",
			});
			if (response.status === "ERROR") {
				throw new Error(response.error || "Failed to delete restricted word.");
			}
			await refresh();
			toast.success("Restricted word deleted");
		} catch (mutationError) {
			toast.error(
				mutationError instanceof Error
					? mutationError.message
					: "Failed to delete restricted word.",
			);
		} finally {
			setMutatingId(null);
		}
	};

	return (
		<main className={styles.adminViewWrapper}>
			<div className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<h1 className="text-2xl font-semibold text-foreground">
						Restricted Words
					</h1>
					<p className="mt-1 text-sm text-muted-foreground">
						Manage the regular-expression rules used to validate narratives.
					</p>
				</div>
				<Button className={styles.adminPrimaryButton} onClick={openCreate}>
					<Plus /> Add restricted word
				</Button>
			</div>

			<section className={styles.settingsSectionFullWidth}>
				<div className="mb-4 flex flex-wrap items-center justify-between gap-3">
					<div className="relative w-full max-w-sm">
						<Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							value={search}
							onChange={(event) => setSearch(event.target.value)}
							placeholder="Search rules or scopes"
							className="pl-9"
						/>
					</div>
					<span className="text-sm text-muted-foreground">
						{filteredRules.length}{" "}
						{filteredRules.length === 1 ? "rule" : "rules"}
					</span>
				</div>

				{isLoading ? (
					<p className={styles.loadingText}>Loading restricted words…</p>
				) : error ? (
					<div className="space-y-3">
						<p className="text-sm text-destructive">{error}</p>
						<Button variant="outline" size="sm" onClick={() => void refresh()}>
							Try again
						</Button>
					</div>
				) : filteredRules.length === 0 ? (
					<p className={styles.emptyText}>
						{rules.length === 0
							? "No restricted words have been configured."
							: "No restricted words match this search."}
					</p>
				) : (
					<div className="overflow-x-auto">
						<table className={styles.dataTable}>
							<thead>
								<tr>
									<th>Name</th>
									<th>Pattern</th>
									<th>Scope</th>
									<th>Behavior</th>
									<th>Enabled</th>
									<th aria-label="Actions" />
								</tr>
							</thead>
							<tbody>
								{filteredRules.map((rule) => (
									<tr
										key={rule.id}
										className={rule.disabled ? "opacity-60" : undefined}
									>
										<td>
											<div className="font-medium">{rule.displayName}</div>
											{rule.suggestion && (
												<div className="max-w-64 truncate text-xs text-muted-foreground">
													{rule.suggestion}
												</div>
											)}
										</td>
										<td>
											<code className="break-all text-xs">
												{rule.regexPattern}
											</code>
										</td>
										<td>{scopeLabel(rule)}</td>
										<td>{rule.warning ? "Warning" : "Block"}</td>
										<td>
											<Switch
												checked={!rule.disabled}
												disabled={mutatingId === rule.id}
												onCheckedChange={(enabled) =>
													void updateDisabled(rule, !enabled)
												}
												aria-label={`${rule.disabled ? "Enable" : "Disable"} ${rule.displayName}`}
											/>
										</td>
										<td>
											<div className="flex justify-end gap-1">
												<Button
													variant="ghost"
													size="icon"
													onClick={() => openEdit(rule)}
													aria-label={`Edit ${rule.displayName}`}
												>
													<Pencil />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="text-destructive hover:text-destructive"
													disabled={mutatingId === rule.id}
													onClick={() => void deleteRule(rule)}
													aria-label={`Delete ${rule.displayName}`}
												>
													<Trash2 />
												</Button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>

			<RuleDialog
				open={dialogOpen}
				rule={editingRule}
				onOpenChange={setDialogOpen}
				onSaved={refresh}
			/>
		</main>
	);
}
